import { ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import { cn } from '../lib/cn.js';

/**
 * A calm progressive-disclosure section: a labelled toggle that reveals its
 * secondary fields. Nothing is removed — it just starts folded so the primary
 * choices stand alone. Keyboard-accessible: the toggle is a real button with
 * `aria-expanded`/`aria-controls`, and the panel is a labelled region.
 */
export function Disclosure({
  label,
  hint,
  defaultOpen = false,
  children,
  className,
}: {
  label: string;
  /** Short grey note shown next to the label, e.g. what hides inside. */
  hint?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="text-ink-soft hover:text-ink flex w-full items-center gap-2 py-1 text-left text-sm transition"
      >
        <ChevronRight
          size={15}
          aria-hidden
          className={cn('shrink-0 transition-transform', open && 'rotate-90')}
        />
        <span className="font-medium">{label}</span>
        {hint && <span className="text-ink-soft/70 truncate text-[11px]">{hint}</span>}
      </button>
      <div id={panelId} hidden={!open} className="pt-3">
        {children}
      </div>
    </div>
  );
}
